import React, { useState, useEffect } from "react";
import {
	Users,
	MessageSquare,
	Activity,
	Clock,
	Star,
	Phone,
	Eye,
	Calendar,
	ChevronDown,
	ChevronUp,
	Search,
	Filter,
	RefreshCw,
	TrendingUp,
	TrendingDown,
	Globe,
	Smartphone,
	BarChart3
} from "lucide-react";
import EnhancedUserDetail from "./EnhancedUserDetail";

const DualUserAnalytics = () => {
	// State for both user types
	const [webUserData, setWebUserData] = useState({
		users: [],
		summary: { totalUsers: 0, activeUsers: 0, avgMessagesPerUser: 0, avgEngagementScore: 0 }
	});
	const [whatsappUserData, setWhatsappUserData] = useState({
		users: [],
		summary: { totalUsers: 0, activeUsers: 0, avgMessagesPerUser: 0, avgEngagementScore: 0 }
	});

	const [activeTab, setActiveTab] = useState('web'); // 'web' or 'whatsapp'
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [timeRange, setTimeRange] = useState('7d');
	const [searchTerm, setSearchTerm] = useState('');
	const [sortField, setSortField] = useState('totalMessages');
	const [sortDirection, setSortDirection] = useState('desc');
	const [selectedUser, setSelectedUser] = useState(null);
	const [showUserDetail, setShowUserDetail] = useState(false);

	const SERVER_URL = 'https://heyakua-whatsapp-bot-655772124305.us-central1.run.app';

	const loadWebUserData = async () => {
		try {
			const response = await fetch(`${SERVER_URL}/analytics/users?range=${timeRange}&limit=50`, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const result = await response.json();
			if (result.success) {
				setWebUserData(result.data);
			} else {
				throw new Error(result.error || 'Failed to load web user data');
			}
		} catch (err) {
			console.error('Error loading web user data:', err);
			throw err;
		}
	};

	const loadWhatsAppUserData = async () => {
		try {
			const response = await fetch(`${SERVER_URL}/analytics/whatsapp-users?range=${timeRange}&limit=50`, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const result = await response.json();
			if (result.success) {
				setWhatsappUserData(result.data);
			} else {
				throw new Error(result.error || 'Failed to load WhatsApp user data');
			}
		} catch (err) {
			console.error('Error loading WhatsApp user data:', err);
			throw err;
		}
	};

	const loadAllData = async () => {
		setLoading(true);
		setError(null);

		try {
			await Promise.all([loadWebUserData(), loadWhatsAppUserData()]);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const loadUserDetail = async (userId, userType) => {
		try {
			const endpoint = userType === 'web'
				? `/analytics/user/${userId}`
				: `/analytics/whatsapp-user/${userId}`;

			const response = await fetch(`${SERVER_URL}${endpoint}`, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const result = await response.json();
			if (result.success) {
				setSelectedUser({...result.data, userType});
				setShowUserDetail(true);
			}
		} catch (err) {
			console.error('Error loading user detail:', err);
		}
	};

	useEffect(() => {
		loadAllData();
	}, [timeRange]);

	// Get current data based on active tab
	const currentData = activeTab === 'web' ? webUserData : whatsappUserData;

	// Filter and sort users
	const filteredUsers = currentData.users
		.filter(user =>
			user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.phoneNumber?.includes(searchTerm)
		)
		.sort((a, b) => {
			const aVal = a[sortField] || 0;
			const bVal = b[sortField] || 0;
			return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
		});

	const handleSort = (field) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
		} else {
			setSortField(field);
			setSortDirection('desc');
		}
	};

	const getEngagementBadge = (score) => {
		if (score >= 10) return { label: 'Excellent', color: 'bg-green-100 text-green-800' };
		if (score >= 5) return { label: 'Good', color: 'bg-blue-100 text-blue-800' };
		if (score >= 2) return { label: 'Fair', color: 'bg-yellow-100 text-yellow-800' };
		return { label: 'Low', color: 'bg-red-100 text-red-800' };
	};

	const getQualityBadge = (quality) => {
		const badges = {
			'Excellent': 'bg-green-100 text-green-800',
			'Good': 'bg-blue-100 text-blue-800',
			'Fair': 'bg-yellow-100 text-yellow-800',
			'Poor': 'bg-red-100 text-red-800'
		};
		return badges[quality] || 'bg-gray-100 text-gray-800';
	};

	const formatDate = (dateStr) => {
		if (!dateStr) return 'Never';
		const date = new Date(dateStr);
		return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	};

	return (
		<div className="p-6 max-w-7xl mx-auto">
			<div className="mb-8">
				<h1 className="text-3xl font-bold text-gray-900 mb-2">User Analytics Dashboard</h1>
				<p className="text-gray-600">Comprehensive analytics for both web app and WhatsApp users</p>
			</div>

			{/* Tab Navigation */}
			<div className="mb-6 border-b border-gray-200">
				<nav className="-mb-px flex space-x-8">
					<button
						onClick={() => setActiveTab('web')}
						className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
							activeTab === 'web'
								? 'border-blue-500 text-blue-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
						}`}
					>
						<Globe className="h-4 w-4" />
						Web App Users
						<span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
							{webUserData.summary.totalUsers}
						</span>
					</button>
					<button
						onClick={() => setActiveTab('whatsapp')}
						className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
							activeTab === 'whatsapp'
								? 'border-green-500 text-green-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
						}`}
					>
						<Smartphone className="h-4 w-4" />
						WhatsApp Users
						<span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
							{whatsappUserData.summary.totalUsers}
						</span>
					</button>
				</nav>
			</div>

			{/* Combined Summary Cards */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
				<div className="bg-white p-6 rounded-lg shadow">
					<div className="flex items-center">
						<div className="flex-shrink-0">
							<Users className="h-8 w-8 text-blue-600" />
						</div>
						<div className="ml-4">
							<p className="text-sm font-medium text-gray-500">Total Users</p>
							<p className="text-2xl font-semibold text-gray-900">
								{webUserData.summary.totalUsers + whatsappUserData.summary.totalUsers}
							</p>
							<p className="text-xs text-gray-500">
								Web: {webUserData.summary.totalUsers} | WhatsApp: {whatsappUserData.summary.totalUsers}
							</p>
						</div>
					</div>
				</div>

				<div className="bg-white p-6 rounded-lg shadow">
					<div className="flex items-center">
						<div className="flex-shrink-0">
							<MessageSquare className="h-8 w-8 text-green-600" />
						</div>
						<div className="ml-4">
							<p className="text-sm font-medium text-gray-500">Avg Messages/User</p>
							<p className="text-2xl font-semibold text-gray-900">
								{Math.round((webUserData.summary.avgMessagesPerUser + whatsappUserData.summary.avgMessagesPerUser) / 2)}
							</p>
							<p className="text-xs text-gray-500">
								Web: {webUserData.summary.avgMessagesPerUser} | WhatsApp: {whatsappUserData.summary.avgMessagesPerUser}
							</p>
						</div>
					</div>
				</div>

				<div className="bg-white p-6 rounded-lg shadow">
					<div className="flex items-center">
						<div className="flex-shrink-0">
							<Activity className="h-8 w-8 text-purple-600" />
						</div>
						<div className="ml-4">
							<p className="text-sm font-medium text-gray-500">Active Users</p>
							<p className="text-2xl font-semibold text-gray-900">
								{webUserData.summary.activeUsers + whatsappUserData.summary.activeUsers}
							</p>
							<p className="text-xs text-gray-500">
								Web: {webUserData.summary.activeUsers} | WhatsApp: {whatsappUserData.summary.activeUsers}
							</p>
						</div>
					</div>
				</div>

				<div className="bg-white p-6 rounded-lg shadow">
					<div className="flex items-center">
						<div className="flex-shrink-0">
							<TrendingUp className="h-8 w-8 text-orange-600" />
						</div>
						<div className="ml-4">
							<p className="text-sm font-medium text-gray-500">Avg Engagement</p>
							<p className="text-2xl font-semibold text-gray-900">
								{Math.round((webUserData.summary.avgEngagementScore + whatsappUserData.summary.avgEngagementScore) / 2 * 10) / 10}
							</p>
							<p className="text-xs text-gray-500">
								Web: {webUserData.summary.avgEngagementScore} | WhatsApp: {whatsappUserData.summary.avgEngagementScore}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Platform-specific Summary */}
			<div className="bg-white p-6 rounded-lg shadow mb-8">
				<h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
					{activeTab === 'web' ? <Globe className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
					{activeTab === 'web' ? 'Web App Users' : 'WhatsApp Users'} - {currentData.summary.totalUsers} total
				</h3>
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<div>
						<p className="text-sm text-gray-500">Active Users</p>
						<p className="text-xl font-semibold">{currentData.summary.activeUsers}</p>
					</div>
					<div>
						<p className="text-sm text-gray-500">Avg Messages</p>
						<p className="text-xl font-semibold">{currentData.summary.avgMessagesPerUser}</p>
					</div>
					<div>
						<p className="text-sm text-gray-500">Engagement Score</p>
						<p className="text-xl font-semibold">{currentData.summary.avgEngagementScore}</p>
					</div>
					<div>
						<p className="text-sm text-gray-500">Platform</p>
						<p className="text-xl font-semibold">{activeTab === 'web' ? 'Web' : 'WhatsApp'}</p>
					</div>
				</div>
			</div>

			{/* Controls */}
			<div className="bg-white p-6 rounded-lg shadow mb-6">
				<div className="flex flex-col md:flex-row gap-4 items-center justify-between">
					<div className="flex items-center gap-4">
						<div className="relative">
							<Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
							<input
								type="text"
								placeholder={`Search ${activeTab} users...`}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>
						<select
							value={timeRange}
							onChange={(e) => setTimeRange(e.target.value)}
							className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
						>
							<option value="1d">Last 24 hours</option>
							<option value="7d">Last 7 days</option>
							<option value="30d">Last 30 days</option>
							<option value="90d">Last 90 days</option>
						</select>
					</div>
					<button
						onClick={loadAllData}
						disabled={loading}
						className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
					>
						<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
						Refresh
					</button>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
					<div className="flex">
						<div className="ml-3">
							<h3 className="text-sm font-medium text-red-800">Error loading data</h3>
							<div className="mt-2 text-sm text-red-700">{error}</div>
						</div>
					</div>
				</div>
			)}

			{/* Users Table */}
			{loading ? (
				<div className="bg-white p-8 rounded-lg shadow text-center">
					<RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
					<p>Loading {activeTab} user data...</p>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th
										onClick={() => handleSort('name')}
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
									>
										<div className="flex items-center gap-1">
											User
											{sortField === 'name' && (sortDirection === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />)}
										</div>
									</th>
									{activeTab === 'web' && (
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Email
										</th>
									)}
									{activeTab === 'whatsapp' && (
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Phone
										</th>
									)}
									<th
										onClick={() => handleSort('totalMessages')}
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
									>
										<div className="flex items-center gap-1">
											Messages
											{sortField === 'totalMessages' && (sortDirection === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />)}
										</div>
									</th>
									<th
										onClick={() => handleSort('totalSessions')}
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
									>
										<div className="flex items-center gap-1">
											Sessions
											{sortField === 'totalSessions' && (sortDirection === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />)}
										</div>
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Quality
									</th>
									<th
										onClick={() => handleSort('engagementScore')}
										className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
									>
										<div className="flex items-center gap-1">
											Engagement
											{sortField === 'engagementScore' && (sortDirection === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />)}
										</div>
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Last Active
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{filteredUsers.length === 0 ? (
									<tr>
										<td colSpan="8" className="px-6 py-4 text-center text-gray-500">
											No {activeTab} users found
										</td>
									</tr>
								) : (
									filteredUsers.map((user) => {
										const engagementBadge = getEngagementBadge(user.engagementScore);
										return (
											<tr key={user.userId} className="hover:bg-gray-50">
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="flex items-center">
														<div className="flex-shrink-0 h-10 w-10">
															<div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
																{activeTab === 'web' ? <Globe className="h-5 w-5 text-blue-600" /> : <Smartphone className="h-5 w-5 text-green-600" />}
															</div>
														</div>
														<div className="ml-4">
															<div className="text-sm font-medium text-gray-900">{user.name}</div>
															<div className="text-sm text-gray-500">ID: {user.userId.slice(-8)}</div>
														</div>
													</div>
												</td>
												{activeTab === 'web' && (
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
														{user.email || 'No email'}
													</td>
												)}
												{activeTab === 'whatsapp' && (
													<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
														{user.phoneNumber || 'Hidden'}
													</td>
												)}
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
													{user.totalMessages}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
													{user.totalSessions}
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getQualityBadge(user.conversationQuality)}`}>
														{user.conversationQuality}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${engagementBadge.color}`}>
														{user.engagementScore} - {engagementBadge.label}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													{formatDate(user.lastActive)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
													<button
														onClick={() => loadUserDetail(user.userId, activeTab)}
														className="text-blue-600 hover:text-blue-900"
													>
														View Details
													</button>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* User Detail Modal */}
			{showUserDetail && selectedUser && (
				<div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
					<div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
						<div className="mt-3">
							<div className="flex items-center justify-between mb-4">
								<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
									{selectedUser.userType === 'web' ? <Globe className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
									{selectedUser.userInfo.name} Details
								</h3>
								<button
									onClick={() => setShowUserDetail(false)}
									className="text-gray-400 hover:text-gray-600"
								>
									<span className="sr-only">Close</span>
									<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<div className="space-y-4">
								<div className="bg-gray-50 p-4 rounded-lg">
									<h4 className="font-medium mb-2">User Information</h4>
									<div className="grid grid-cols-2 gap-4 text-sm">
										<div>
											<span className="text-gray-500">Platform:</span>
											<span className="ml-2 font-medium">{selectedUser.userType === 'web' ? 'Web App' : 'WhatsApp'}</span>
										</div>
										<div>
											<span className="text-gray-500">User ID:</span>
											<span className="ml-2 font-medium">{selectedUser.userInfo.userId}</span>
										</div>
										{selectedUser.userInfo.email && (
											<div>
												<span className="text-gray-500">Email:</span>
												<span className="ml-2 font-medium">{selectedUser.userInfo.email}</span>
											</div>
										)}
										{selectedUser.userInfo.phoneNumber && (
											<div>
												<span className="text-gray-500">Phone:</span>
												<span className="ml-2 font-medium">{selectedUser.userInfo.phoneNumber}</span>
											</div>
										)}
										<div>
											<span className="text-gray-500">Created:</span>
											<span className="ml-2 font-medium">{formatDate(selectedUser.userInfo.createdAt)}</span>
										</div>
										<div>
											<span className="text-gray-500">Last Active:</span>
											<span className="ml-2 font-medium">{formatDate(selectedUser.userInfo.lastActive)}</span>
										</div>
									</div>
								</div>

								{selectedUser.userType === 'web' && selectedUser.sessions && (
									<div className="bg-gray-50 p-4 rounded-lg">
										<h4 className="font-medium mb-2">Recent Sessions ({selectedUser.sessions.length})</h4>
										<div className="max-h-48 overflow-y-auto">
											{selectedUser.sessions.slice(0, 5).map((session, index) => (
												<div key={index} className="border-b border-gray-200 pb-2 mb-2 last:border-b-0">
													<div className="flex justify-between">
														<span className="text-sm font-medium">Session {session.sessionId}</span>
														<span className="text-xs text-gray-500">{formatDate(session.createdAt)}</span>
													</div>
													<div className="text-xs text-gray-600">
														{session.messageCount} messages
													</div>
												</div>
											))}
										</div>
									</div>
								)}

								{selectedUser.userType === 'whatsapp' && selectedUser.conversationFlows && (
									<div className="bg-gray-50 p-4 rounded-lg">
										<h4 className="font-medium mb-2">Conversation Flows ({selectedUser.conversationFlows.length})</h4>
										<div className="max-h-48 overflow-y-auto">
											{selectedUser.conversationFlows.slice(0, 5).map((flow, index) => (
												<div key={index} className="border-b border-gray-200 pb-2 mb-2 last:border-b-0">
													<div className="flex justify-between">
														<span className="text-sm font-medium">{flow.flowType} Flow</span>
														<span className="text-xs text-gray-500">{formatDate(flow.createdAt)}</span>
													</div>
													<div className="text-xs text-gray-600">
														{flow.messageCount} messages - Status: {flow.status}
													</div>
												</div>
											))}
										</div>
									</div>
								)}

								{/* Metrics for both types */}
								<div className="bg-gray-50 p-4 rounded-lg">
									<h4 className="font-medium mb-2">Performance Metrics</h4>
									<div className="grid grid-cols-2 gap-4 text-sm">
										<div>
											<span className="text-gray-500">Total Sessions:</span>
											<span className="ml-2 font-medium">{selectedUser.totalSessions || selectedUser.metrics?.totalSessions || 0}</span>
										</div>
										<div>
											<span className="text-gray-500">Total Messages:</span>
											<span className="ml-2 font-medium">{selectedUser.totalMessages || selectedUser.metrics?.totalMessages || 0}</span>
										</div>
										<div>
											<span className="text-gray-500">Avg Messages/Session:</span>
											<span className="ml-2 font-medium">{selectedUser.avgMessagesPerSession || selectedUser.metrics?.avgMessagesPerSession || 0}</span>
										</div>
										{selectedUser.userType === 'whatsapp' && selectedUser.metrics && (
											<>
												<div>
													<span className="text-gray-500">Delivery Rate:</span>
													<span className="ml-2 font-medium">{selectedUser.metrics.deliveryRate}%</span>
												</div>
												<div>
													<span className="text-gray-500">Read Rate:</span>
													<span className="ml-2 font-medium">{selectedUser.metrics.readRate}%</span>
												</div>
												<div>
													<span className="text-gray-500">Avg Response Time:</span>
													<span className="ml-2 font-medium">{selectedUser.metrics.avgResponseTime}s</span>
												</div>
											</>
										)}
									</div>
								</div>
							</div>

							<div className="mt-6 flex justify-end">
								<button
									onClick={() => setShowUserDetail(false)}
									className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
								>
									Close
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DualUserAnalytics;